import { Starter, MANAGEMENT_STRATEGIES } from '../types/baking';
import { addNotification } from './notificationStore';
import { getLastPushTime, setLastPushTime } from './pushNotificationTracker';

const lastNotificationTimes = new Map<string, number>();

export function getTemperatureAdjustedInterval(starter: Starter, baseInterval: number): number {
  if (starter.storage === 'fridge' || !starter.roomTemperature) {
    return baseInterval;
  }

  const temp = starter.roomTemperature;

  if (temp >= 16 && temp < 20) {
    // Cold room: Add 2 hours to interval
    return baseInterval + 2;
  } else if (temp > 25 && temp <= 30) {
    // Warm room: Subtract 1 hour from interval
    return Math.max(baseInterval - 1, 1); // Ensure interval is at least 1 hour
  }

  // Normal temperature range (20-25°C): No adjustment
  return baseInterval;
}

export function getTemperatureWarning(temperature: number): string | null {
  if (temperature < 16) {
    return 'This environment is too cold for a starter to thrive. Try placing your starter near a heating source or use your oven preheated to 30°C as a fermentation chamber.';
  }
  if (temperature > 30) {
    return 'This environment is too warm for a starter to thrive. Try finding a cooler area in your house or move to the fridge 2 hours after feeding to prevent over-fermentation.';
  }
  return null;
}

export function checkStarterHealth(starter: Starter) {
  if (!starter.lastFed) {
    return {
      status: 'critical',
      message: 'Starter has never been fed',
      requiresUrgentFeed: true
    };
  }

  const hoursSinceLastFeed = 
    (new Date().getTime() - new Date(starter.lastFed).getTime()) / (1000 * 60 * 60);

  // Use normal mode as default if no management strategy is set
  const strategy = MANAGEMENT_STRATEGIES[starter.managementStrategy || 'normal'];
  const baseInterval = starter.storage === 'room' 
    ? strategy.feedingInterval.room 
    : strategy.feedingInterval.fridge;

  // Apply temperature adjustment if stored at room temperature
  const maxInterval = starter.storage === 'room'
    ? getTemperatureAdjustedInterval(starter, baseInterval)
    : baseInterval;

  // Only trigger urgent feed when we've actually exceeded the feeding interval
  if (hoursSinceLastFeed > maxInterval) {
    return {
      status: 'critical',
      message: 'Starter needs immediate feeding',
      requiresUrgentFeed: true
    };
  }

  // Warning threshold at 90% of max interval
  const warningThreshold = maxInterval * 0.9;
  if (hoursSinceLastFeed >= warningThreshold) {
    return {
      status: 'warning',
      message: `Feed starter within ${Math.ceil(maxInterval - hoursSinceLastFeed)} hours`,
      requiresUrgentFeed: false
    };
  }

  return {
    status: 'healthy',
    message: 'Starter is healthy',
    requiresUrgentFeed: false
  };
}

export async function checkAndNotifyStarterFeeding(starter: Starter, isLoginCheck: boolean = false) {
  const health = checkStarterHealth(starter);

  if (health.requiresUrgentFeed && starter.userId) {
    try {
      const now = Date.now();

      let lastNotificationTime = 0;
      try {
        lastNotificationTime = await getLastPushTime(starter.userId, starter.id);
      } catch {
        lastNotificationTime = lastNotificationTimes.get(starter.id) || 0;
      }

      const timeSinceLastNotification = now - lastNotificationTime;

      console.log('Checking starter:', starter.name, {
        timeSinceLastNotification: Math.round(timeSinceLastNotification / (1000 * 60 * 60)),
        requiresUrgentFeed: health.requiresUrgentFeed,
        hoursSinceLastFeed: (now - new Date(starter.lastFed).getTime()) / (1000 * 60 * 60),
        maxInterval: starter.storage === 'room'
          ? getTemperatureAdjustedInterval(starter, MANAGEMENT_STRATEGIES[starter.managementStrategy || 'normal'].feedingInterval.room)
          : MANAGEMENT_STRATEGIES[starter.managementStrategy || 'normal'].feedingInterval.fridge
      });

      const minTimeBetweenNotifications = isLoginCheck ? 0 : 1 * 60 * 60 * 1000;

      if (timeSinceLastNotification >= minTimeBetweenNotifications) {
        console.log('Sending notification for starter:', starter.name, 'isLoginCheck:', isLoginCheck);

        const notificationData = {
          type: 'starter_feeding' as const,
          title: 'Urgent Starter Feed Required!',
          message: `${starter.name} needs immediate feeding to prevent starter death`,
          data: {
            starterId: starter.id,
            starterName: starter.name,
            route: '/starter'
          },
          expiresAt: new Date(now + 6 * 60 * 60 * 1000)
        };

        await addNotification(starter.userId, notificationData, isLoginCheck);
        lastNotificationTimes.set(starter.id, now);
        try {
          await setLastPushTime(starter.userId, starter.id, now);
        } catch {
          console.log('Failed to persist notification time to Firestore');
        }

        console.log('Notification sent for starter:', starter.name);
        return true;
      } else {
        console.log('Skipping notification for starter:', starter.name, '(too soon)');
      }
    } catch (error) {
      console.error('Error sending starter feed notifications:', error);
    }
  }

  return false;
}